import wretch, { Wretch, WretchAddon } from 'wretch';
import FormDataAddon from 'wretch/addons/formData';
import QueryStringAddon from 'wretch/addons/queryString';
import { validate as isValidUUID } from 'uuid';
import { ApiUrls, SugarMoneyApiEnvironmentsEnum, VersionEnum } from './constants';
import { Auth } from './modules/auth';
import { PublicKey } from '@solana/web3.js';
import { SugarMoneyLoginSignMessageType } from './types/auth';

import { makeApiRequest } from './services/api';
import { coinModule } from './modules/coin';
import { coinsModule } from './modules/coins';
import { WretchWithFormData } from './types/wretch';
import {
  BuildModuleType,
  SugarMoneyApiCheckTransactionStatusType,
  SugarMoneyApiMakeApiRequestType,
} from 'types/api-internal';
import { userModule } from './modules/user';
import { priceModule } from './modules/price';
import { gameModule } from './modules/game';
import { gamesModule } from './modules/games';
import { ranksModule } from './modules/ranks';
import { ticketsModule } from './modules/tickets';
import { feesModule } from './modules/fees';
import { websocketModule } from './modules/websocket';
import { referralsModule } from './modules/referrals';
import { transactionsModule } from './modules/transactions';

const w = wretch().addon(FormDataAddon).addon(QueryStringAddon) as WretchWithFormData;

/**
 * The main class to interact with the SugarMoney API.
 *
 * @example
 * ```ts
 * const api = new SugarMoneyApi({ environment: ApiEnvironmentsEnum.Preprod });
 *
 * await api.login(args);
 * ```
 */
export class SugarMoneyApi {
  private apiInstanceV1: WretchWithFormData;
  private authApiInstance: WretchWithFormData;
  private environment: SugarMoneyApiEnvironmentsEnum;
  private authToken: string | null;
  private sdkId: string | null;
  private makeApiRequestV1: SugarMoneyApiMakeApiRequestType;
  public coin: ReturnType<typeof coinModule>;
  public coins: ReturnType<typeof coinsModule>;
  public user: ReturnType<typeof userModule>;
  public price: ReturnType<typeof priceModule>;
  public game: ReturnType<typeof gameModule>;
  public games: ReturnType<typeof gamesModule>;
  public ranks: ReturnType<typeof ranksModule>;
  public tickets: ReturnType<typeof ticketsModule>;
  public fees: ReturnType<typeof feesModule>;
  public referrals: ReturnType<typeof referralsModule>;
  public websocket: ReturnType<typeof websocketModule>;
  public transactions: ReturnType<typeof transactionsModule>;

  constructor({
    environment = SugarMoneyApiEnvironmentsEnum.Preprod,
    authToken,
    sdkId,
  }: {
    environment?: SugarMoneyApiEnvironmentsEnum;
    authToken?: string;
    sdkId?: string;
  }) {
    if (sdkId && !isValidUUID(sdkId)) {
      throw new Error('sdkId must be a valid UUID (e.g., "550e8400-e29b-41d4-a716-446655440000")');
    }

    this.environment = environment;
    this.authToken = authToken ?? null;
    this.sdkId = sdkId ?? null;

    this.apiInstanceV1 = w
      .url(ApiUrls[environment] + '/api/' + VersionEnum.V1 + '/')
      .auth(this.authToken ? `${this.authToken}` : '') as WretchWithFormData;

    this.authApiInstance = w
      .url(ApiUrls[environment] + '/api/auth/')
      .auth(this.authToken ? `${this.authToken}` : '') as WretchWithFormData;

    if (this.sdkId) {
      this.apiInstanceV1 = this.apiInstanceV1.headers({
        'sdk-id': this.sdkId,
      }) as WretchWithFormData;
      this.authApiInstance = this.authApiInstance.headers({
        'sdk-id': this.sdkId,
      }) as WretchWithFormData;
    }

    this.makeApiRequestV1 = (data) =>
      makeApiRequest({
        ...data,
        wretchInstance: this.apiInstanceV1,
        environment,
        token: this.authToken,
      });

    const buildModule = <T>({
      module,
      checkTransactionStatus,
    }: {
      module: BuildModuleType<T>;
      checkTransactionStatus?: SugarMoneyApiCheckTransactionStatusType;
    }) =>
      module({
        makeApiRequest: this.makeApiRequestV1,
        apiInstance: this.apiInstanceV1,
        checkTransactionStatus,
      });

    this.transactions = buildModule({
      module: transactionsModule,
    });

    this.coin = buildModule({
      module: coinModule,
      checkTransactionStatus: this.transactions.checkStatus,
    });

    this.game = buildModule({
      module: gameModule,
      checkTransactionStatus: (data) => this.transactions.checkStatus(data),
    });

    this.tickets = buildModule({
      module: ticketsModule,
      checkTransactionStatus: this.transactions.checkStatus,
    });

    this.referrals = buildModule({
      module: referralsModule,
      checkTransactionStatus: this.transactions.checkStatus,
    });

    this.coins = buildModule({
      module: coinsModule,
    });

    this.user = buildModule({ module: userModule });

    this.price = buildModule({ module: priceModule });

    this.games = buildModule({ module: gamesModule });

    this.ranks = buildModule({ module: ranksModule });

    this.fees = buildModule({ module: feesModule });

    // WebSocket module doesn't use the same pattern as it doesn't need makeApiRequest
    this.websocket = websocketModule({ environment });
  }

  /**
   * Login to the API - the token lasts 30 days.
   *
   * You do not need to pass the token to other methods, the SDK will handle it.
   * Just make sure to call this and that it goes through successfuly.
   *
   * @param publicKey - The public wallet public key
   * @param signMessageFn - The function to sign a message to prove you own the wallet
   * @returns The auth token
   */
  async login({
    publicKey,
    signMessageFn,
  }: {
    publicKey: PublicKey;
    signMessageFn: SugarMoneyLoginSignMessageType;
  }) {
    const token = await Auth.login({
      publicKey,
      apiInstance: this.authApiInstance,
      signMessageFn,
      baseUrl: ApiUrls[this.environment],
    });

    this.authToken = token;

    return token;
  }
}
